import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { PageHeader } from '@/app/DashboardLayout';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, ErrorState, Input, SkeletonTable } from '@/features/common/ui';

export interface AccountStatus {
  email: string;
  deletionDueAt: string | null;
  blocked: string | null;
  graceDays: number;
  books: Array<{ orgId: string; name: string; isAdmin: boolean; fate: 'deleted' | 'left'; others: Array<{ name: string; isAdmin: boolean }> }>;
}

export const ACCOUNT_KEY = ['account'] as const;

/** "13 November 2026". */
export function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Settings -> Your account: download your books, and delete the account.
 *
 * ── THE ORDER ON THE PAGE IS THE ADVICE ──
 * Downloads first, deletion last. The law makes a business keep its books
 * for years, and that duty does not end because the software did; a person
 * who reads top to bottom has been offered a copy of every book before the
 * button that removes them. The deletion itself waits 30 days (the privacy
 * policy's promise), and the page says, book by book, which go and which
 * stay with another owner - naming the people who will lose a book they
 * can open today, because they are the ones who will be surprised.
 */
export function AccountPage() {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ACCOUNT_KEY, queryFn: () => api.get<AccountStatus>('/account') });
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: () => api.post<AccountStatus>('/account/deletion', { password }),
    onSuccess: (data) => {
      queryClient.setQueryData(ACCOUNT_KEY, data);
      setPassword('');
      setConfirming(false);
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.delete<AccountStatus>('/account/deletion'),
    onSuccess: (data) => queryClient.setQueryData(ACCOUNT_KEY, data),
  });

  const download = async (orgId: string) => {
    setDownloading(orgId);
    setDownloadError(null);
    try {
      const res = await fetch(`/v1/account/books/${orgId}/export`, { credentials: 'include' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message || `The download failed (${res.status}).`);
      }
      const named = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = named || 'fint-book.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'The download failed.');
    } finally {
      setDownloading(null);
    }
  };

  if (status.isLoading) return <SkeletonTable />;
  if (status.isError || !status.data) return <ErrorState error={status.error} />;
  const s = status.data;
  const owned = s.books.filter((b) => b.isAdmin);
  const goes = s.books.filter((b) => b.fate === 'deleted');
  const stays = s.books.filter((b) => b.fate === 'left');
  const passwordError = request.error instanceof ApiError ? request.error : undefined;

  return (
    <>
      <PageHeader title="Your account" subtitle={s.email} />
      <div className="max-w-2xl space-y-4 px-4 pb-10 sm:px-5">
        {s.deletionDueAt ? (
          <div role="status" data-account-leaving className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">
            <p className="font-medium">Your account will be deleted on {longDate(s.deletionDueAt)}.</p>
            <p className="mt-1 text-muted-foreground">
              Until then everything works as before. Download what you need below, or cancel if you have changed your mind.
            </p>
            <Button className="mt-3" variant="outline" loading={cancel.isPending} onClick={() => cancel.mutate()} data-account-cancel>
              Keep my account
            </Button>
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Download your books</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Everything in a book, as one ZIP: a file per kind of record, and every receipt and attachment. Indian law asks a
              business to keep its accounts for years, so keep a copy before you leave, and now and then anyway.
            </p>
            {owned.length ? (
              <ul className="divide-y rounded-md border">
                {owned.map((b) => (
                  <li key={b.orgId} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 truncate font-medium">{b.name}</span>
                    <Button size="sm" variant="outline" loading={downloading === b.orgId} onClick={() => { download(b.orgId).catch(() => undefined); }} data-account-download={b.name}>
                      <Download className="size-4" aria-hidden />
                      Download
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>You are not an owner of any book. Ask a book&rsquo;s owner for a copy of it.</p>
            )}
            {downloadError ? (
              <p role="alert" className="text-destructive">
                {downloadError}
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Delete your account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {s.blocked ? (
              <p className="text-muted-foreground">{s.blocked}</p>
            ) : (
              <>
                <p className="text-muted-foreground">
                  Nothing is deleted at once. Your account and the books below are deleted {s.graceDays} days after you ask, and
                  you can cancel until then.
                </p>
                {goes.length ? (
                  <div className="space-y-1.5" data-account-goes>
                    <p className="font-medium">Deleted with everything in them, because you are their only owner:</p>
                    <ul className="space-y-1.5 pl-1">
                      {goes.map((b) => (
                        <li key={b.orgId}>
                          <span className="font-medium">{b.name}</span>
                          {b.others.length ? (
                            <span className="text-muted-foreground">
                              {' '}
                              - {b.others.map((o) => o.name).join(', ')} will lose access too. To keep the book, make one of them an
                              owner in <Link className="text-primary hover:underline" data-tap-exempt="text link in a sentence" to="/settings/members">Members</Link> first.
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {stays.length ? (
                  <div className="space-y-1.5" data-account-stays>
                    <p className="font-medium">Kept by their other owners; you are removed from them:</p>
                    <p className="flex flex-wrap gap-1.5">
                      {stays.map((b) => (
                        <Badge key={b.orgId} variant="outline">
                          {b.name}
                        </Badge>
                      ))}
                    </p>
                  </div>
                ) : null}

                {s.deletionDueAt ? null : confirming ? (
                  <form
                    className="space-y-3 rounded-md border border-destructive/40 p-3"
                    onSubmit={(e) => {
                      e.preventDefault();
                      request.mutate();
                    }}
                  >
                    <Input
                      label="Your password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={passwordError?.fieldError('password') ?? (passwordError && !passwordError.errors?.length ? passwordError.message : undefined)}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button type="submit" variant="destructive" loading={request.isPending} data-account-delete-confirm>
                        Delete my account in {s.graceDays} days
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button variant="outline" className="text-destructive" onClick={() => setConfirming(true)} data-account-delete>
                    Delete my account
                  </Button>
                )}
              </>
            )}
            <p className="text-xs text-muted-foreground">
              What is kept and for how long: <Link className="text-primary hover:underline" data-tap-exempt="text link in a sentence" to="/privacy">privacy policy</Link>.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/**
 * On every screen while an account is on its way out: the one reminder that
 * cannot be missed, with the way back one tap away. Asked for rarely - the
 * date does not move - and shows nothing for everyone else.
 */
export function LeavingBanner() {
  const status = useQuery({ queryKey: ACCOUNT_KEY, queryFn: () => api.get<AccountStatus>('/account'), staleTime: 5 * 60_000 });
  const due = status.data?.deletionDueAt;
  if (!due) return null;
  return (
    <p
      role="status"
      className="mx-4 mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm sm:mx-5 print:hidden"
      data-testid="leaving-banner"
    >
      Your Fint account will be deleted on {longDate(due)}.{' '}
      <Link className="inline-flex touch:min-h-11 items-center font-medium text-primary hover:underline" to="/settings/account">
        Download your books or keep your account
      </Link>
    </p>
  );
}
